import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { scaffolderPlugin, ScaffolderPage } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
// import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { CustomSettingsPage } from './components/settings/CustomSettingsPage';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { CustomLayout } from './components/layout/CustomLayout';

import {
  AutoLogout,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { CustomAlertDisplay } from './components/common/CustomAlertDisplay';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';

import { UnifiedThemeProvider} from '@backstage/theme';
import { baseLightTheme, baseDarkTheme, customTheme } from './themes';
import BrightnessAutoIcon from '@material-ui/icons/BrightnessAuto';
import { PichinchaSignIn } from './components/login/PichinchaSignIn';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';

// Importación de componentes personalizados
import { AnnouncementsAdminGuard, HideNewAnnouncementButton, HideCategoryWriteActions, HideAnnouncementsAdminMenu } from './components/announcements';
import { AdminOnlyComponent } from './components/auth/PermissionWrapper';
import { LinksDeInteres } from './components/linksDeInteres';
import { Dashboard } from './components/dashboard';
import { CreateComponent } from './components/create';

// Importación de estilos personalizados
//import './styles/hideHeader.css';
import './styles/pichinchaStyles.css';
import './styles/layoutFix.css';
import { ScaffolderPageWrapper } from './components/scaffolder/ScaffolderPageWrapper';
import { AzureDevOpsProjectPickerFieldExtension, AzureDevOpsRepositoryPickerFieldExtension, AzureDevOpsBranchPickerFieldExtension } from './scaffolder/AzureDevOpsPicker';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      return <PichinchaSignIn {...props} />;
    },
  },
  themes: [
    // {
    //   id: 'base-light-theme',
    //   title: 'Base Light Theme',
    //   variant: 'light',
    //   icon: <LightIcon />,
    //   Provider: ({ children }) => (
    //     <UnifiedThemeProvider theme={baseLightTheme} children={children} />
    //   ),
    // },
    {
      id: 'custom-theme',
      title: 'Banco Pichincha Theme',
      variant: 'light',
      icon: <BrightnessAutoIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customTheme} children={children} />
      ),
    },
    // ,
    // {
    //   id: 'base-dark-theme',
    //   title: 'Base Dark Theme',
    //   variant: 'dark',
    //   icon: <DarkIcon />,
    //   Provider: ({ children }) => (
    //     <UnifiedThemeProvider theme={baseDarkTheme} children={children} />
    //   ),
    // }
  ]
});

const routes = (
  <FlatRoutes>
    {/* <Route path="/" element={<Navigate to="catalog" />} /> */}
    <Route path="/" element={<Dashboard />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    {/* Scaffolder - acceso directo */}
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <AzureDevOpsProjectPickerFieldExtension />
        <AzureDevOpsRepositoryPickerFieldExtension />
        <AzureDevOpsBranchPickerFieldExtension />
        {/* Aquí puedes agregar otras extensiones */}
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/template" element={<CreateComponent />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <AdminOnlyComponent
          fallback={
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>Acceso Restringido</h2>
              <p>Solo los administradores pueden importar componentes al catálogo.</p>
              <p>Si necesitas importar un componente, contacta al equipo de DevOps.</p>
            </div>
          }
        >
          <RequirePermission permission={catalogEntityCreatePermission}>
            <CatalogImportPage />
          </RequirePermission>
        </AdminOnlyComponent>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/settings" element={<CustomSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/announcements/admin" element={
      <AnnouncementsAdminGuard>
        <AnnouncementsPage />
      </AnnouncementsAdminGuard>
    } />
    <Route path="/links" element={<LinksDeInteres />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AutoLogout/>
    <CustomAlertDisplay transientTimeoutMs={5000} />
    <OAuthRequestDialog />
    <HideNewAnnouncementButton />
    <HideCategoryWriteActions />
    <HideAnnouncementsAdminMenu />
    <SignalsDisplay />
    <AppRouter>
      <CustomLayout>
        {routes}
      </CustomLayout>
    </AppRouter>
  </>,
);